import React, {useEffect, useState} from 'react';
import {App, Col, Row, Skeleton, Space, Typography} from 'antd';
import {InfoCircleOutlined, MailOutlined} from '@ant-design/icons';
import './OlympiadList.less';
import {useNavigate} from "react-router";
import {getRequest} from "../../../../api/httpRequest";
import OlympiadCard from "../OlympiadCard";
import RegistrationModal from "../RegistrationModal";
import dayjs from "dayjs";

const {Title, Text} = Typography;

// Интерфейс для данных об олимпиаде
interface Olympiad {
    id: number;
    subject: string;
    period: string;
    deadline: Date;
    grades: string;
    isRegistered?: boolean;
    olympiadDate?: Date;
    firstTourPass?: boolean | null;
}

// Основной компонент страницы
const OlympiadList: React.FC = () => {
    const [olympiads, setOlympiads] = useState<Olympiad[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentOlympiad, setCurrentOlympiad] = useState<Olympiad | null>(null);
    const navigate = useNavigate();
    const {notification} = App.useApp()

    useEffect(() => {
        getRequest('/server/olympiad/list')
            .then(response => {
                setOlympiads(response.data.map((olympiad: any) => {
                    return {
                        id: olympiad.id,
                        subject: olympiad.discipline,
                        period: olympiad.period,
                        deadline: dayjs(olympiad.dateEnd).toDate(),
                        olympiadDate: new Date(olympiad.olympiadDate),
                        grades: olympiad.grade,
                        isRegistered: olympiad.registered,
                        firstTourPass: olympiad.firstTourPass // Добавляем поле из ответа сервера
                    }
                }));
            })
            .catch(error => {
                notification.error({
                    message: 'Ошибка загрузки',
                    description: 'Не удалось загрузить список олимпиад'
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // Функция для обновления статуса олимпиады после записи
    const updateOlympiadStatus = (olympiadId: number) => {
        setOlympiads(prevOlympiads => 
            prevOlympiads.map(olympiad => 
                olympiad.id === olympiadId 
                    ? { ...olympiad, isRegistered: true }
                    : olympiad
            )
        );
    };

    // Функция для открытия модального окна
    const handleRegistrationClick = (olympiad: Olympiad) => {
        if (isOlympiadLocked(olympiad)) {
            notification.warning({
                message: 'Невозможно записаться',
                description: `Вы уже записаны на олимпиаду по ${olympiad.subject}. Можно записаться только на одну олимпиаду по каждой дисциплине.`,
                duration: 5
            });
            return;
        }
        setCurrentOlympiad(olympiad);
        setIsModalVisible(true);
    };

    // Функция для перехода к деталям олимпиады
    const handleViewDetails = (olympiad: Olympiad) => {
        // Разрешаем переход если пользователь записан на олимпиаду,
        // независимо от сроков регистрации или проведения
        if (olympiad.isRegistered) {
            navigate('/olympiad/details', {state: {olympiad: olympiad}});
            return;
        }

        // Для незаписанных пользователей проверяем сроки
        if (isRegistrationOver(olympiad.deadline)) {
            notification.warning({
                message: 'Регистрация закрыта',
                description: 'Срок регистрации на эту олимпиаду истек.'
            });
            return;
        }

        if (isOlympiadOver(olympiad.olympiadDate || new Date())) {
            notification.warning({
                message: 'Олимпиада завершена',
                description: 'Эта олимпиада уже завершилась.'
            });
            return;
        }

        navigate('/olympiad/details', {state: {olympiad: olympiad}});
    };

    // Функция для закрытия модального окна
    const handleModalClose = () => {
        setIsModalVisible(false);
        setCurrentOlympiad(null);
    };

    // Функция для обработки успешной записи
    const handleRegistrationSuccess = (olympiadId: number) => {
        updateOlympiadStatus(olympiadId);
        handleModalClose();
        
        // Показать уведомление об успешной записи
        notification.success({
            message: 'Запись успешна',
            description: 'Вы успешно записаны на олимпиаду!',
            duration: 3
        });
    };

    // Получаем список дисциплин, на которые пользователь уже записан
    const getRegisteredSubjects = () => {
        return olympiads
            .filter(olympiad => olympiad.isRegistered)
            .map(olympiad => olympiad.subject);
    };

    // Проверяем, заблокирована ли олимпиада
    const isOlympiadLocked = (olympiad: Olympiad) => {
        const registeredSubjects = getRegisteredSubjects();
        return registeredSubjects.includes(olympiad.subject) && !olympiad.isRegistered;
    };

    // Функция для проверки, окончена ли запись
    const isRegistrationOver = (deadline: Date): boolean => {
        return new Date() > deadline;
    };

    // Функция для проверки, прошла ли дата олимпиады
    const isOlympiadOver = (olympiadDate: Date): boolean => {
        return new Date() > olympiadDate;
    };

    // Группировка олимпиад по классам
    const olympiadsByGrade = {
        grade11: olympiads.filter(olympiad => olympiad.grades === '11'),
        grade9_10: olympiads.filter(olympiad => olympiad.grades === '9-10'),
    };

    // Цвета для разных классов
    const gradeColors: Record<string, string> = {
        '11': '#1890ff',
        '9-10': '#52c41a',
    };

    if (loading) {
        return (
            <div className="olympiad-page">
                <div className="olympiad-container">
                    <Skeleton active paragraph={{rows: 4}}/>
                    <Skeleton active paragraph={{rows: 4}}/>
                </div>
            </div>
        );
    }

    return (
        <div className="olympiad-page">
            <div className="olympiad-container">
                <Title level={2} className="page-title">
                    Запись на школьные олимпиады
                </Title>

                <div className="info-block">
                    <Space>
                        <InfoCircleOutlined/>
                        <Text>
                            Можно записаться только на одну олимпиаду по
                            каждой дисциплине.
                            По вопросам: <a
                            href="mailto:fdp@rut-miit.ru "><MailOutlined/> fdp@rut-miit.ru </a>
                        </Text>
                    </Space>
                </div>

                {/* Олимпиады для 11 класса */}
                <Title level={4} className="grade-title grade-11">
                    11 класс
                </Title>

                <Row gutter={[16, 16]} className="grade-row">
                    {olympiadsByGrade.grade11.map(olympiad => (
                        <Col xs={24} sm={12} key={olympiad.id}>
                            <OlympiadCard
                                olympiad={olympiad}
                                onRegister={handleRegistrationClick}
                                onViewDetails={handleViewDetails}
                                isRegistrationOver={isRegistrationOver(olympiad.deadline)}
                                isOlympiadOver={isOlympiadOver(olympiad.olympiadDate || new Date())}
                                isLocked={isOlympiadLocked(olympiad)}
                                gradeColor={gradeColors[olympiad.grades]}
                                firstTourPass={olympiad.firstTourPass}
                            />
                        </Col>
                    ))}
                </Row>

                {/* Олимпиады для 9-10 классов */}
                <Title level={4} className="grade-title grade-9-10">
                    9-10 классы
                </Title>

                <Row gutter={[16, 16]} className="grade-row">
                    {olympiadsByGrade.grade9_10.map(olympiad => (
                        <Col xs={24} sm={12} key={olympiad.id}>
                            <OlympiadCard
                                olympiad={olympiad}
                                onRegister={handleRegistrationClick}
                                onViewDetails={handleViewDetails}
                                isRegistrationOver={isRegistrationOver(olympiad.deadline)}
                                isOlympiadOver={isOlympiadOver(olympiad.olympiadDate || new Date())}
                                isLocked={isOlympiadLocked(olympiad)}
                                gradeColor={gradeColors[olympiad.grades]}
                                firstTourPass={olympiad.firstTourPass}
                            />
                        </Col>
                    ))}
                </Row>

                {/* Модальное окно для записи */}
                <RegistrationModal
                    visible={isModalVisible}
                    onClose={handleModalClose}
                    onSuccess={handleRegistrationSuccess}
                    olympiad={currentOlympiad}
                    gradeColors={gradeColors}
                />
            </div>
        </div>
    );
};

export default OlympiadList;